// Server-Sent Events client for GET /api/v1/stream.
//
// We use fetch + ReadableStream rather than the built-in EventSource so we can control
// exponential backoff, send `Last-Event-ID` on reconnect for ring-buffer resume, and run a
// watchdog that force-reconnects on a silently-dead stream. The frame parser is a pure,
// independently-tested class.

// ---- Frame parser ---------------------------------------------------------------------

export interface SseFrame {
  event: string | null;
  id: string | null;
  data: string;
  comment: boolean;
}

/**
 * Incremental SSE wire parser. Feed it decoded text chunks; it returns whole frames.
 * Handles CRLF/LF, multi-line `data:`, `event:`, `id:`, and `:comment` keep-alives
 * (emitted immediately as `comment` frames so the caller can treat them as liveness pings).
 */
export class SSEParser {
  private buf = '';
  private event: string | null = null;
  private id: string | null = null;
  private dataLines: string[] = [];

  push(chunk: string): SseFrame[] {
    this.buf += chunk;
    const frames: SseFrame[] = [];
    let idx: number;
    while ((idx = this.buf.indexOf('\n')) !== -1) {
      let line = this.buf.slice(0, idx);
      this.buf = this.buf.slice(idx + 1);
      if (line.endsWith('\r')) line = line.slice(0, -1);

      if (line === '') {
        // Blank line dispatches the accumulated event (if any).
        if (this.dataLines.length > 0 || this.event !== null || this.id !== null) {
          frames.push({
            event: this.event,
            id: this.id,
            data: this.dataLines.join('\n'),
            comment: false,
          });
        }
        this.event = null;
        this.id = null;
        this.dataLines = [];
        continue;
      }

      if (line.startsWith(':')) {
        // Comment / keep-alive. Emit so the caller can reset its watchdog.
        frames.push({ event: null, id: null, data: line.slice(1).trimStart(), comment: true });
        continue;
      }

      const colon = line.indexOf(':');
      const field = colon === -1 ? line : line.slice(0, colon);
      let value = colon === -1 ? '' : line.slice(colon + 1);
      if (value.startsWith(' ')) value = value.slice(1);

      switch (field) {
        case 'event':
          this.event = value;
          break;
        case 'data':
          this.dataLines.push(value);
          break;
        case 'id':
          // Per spec a NUL in the id is ignored; otherwise record it.
          if (!value.includes('\0')) this.id = value;
          break;
        case 'retry':
          // Backoff is managed by the client; ignore server hints.
          break;
        default:
          break;
      }
    }
    return frames;
  }
}

// ---- Streaming client -----------------------------------------------------------------

export class SseHttpError extends Error {
  readonly status: number;
  constructor(status: number) {
    super('SSE HTTP ' + status);
    this.name = 'SseHttpError';
    this.status = status;
  }
}

export interface SseClientOptions {
  url: string;
  /** A dispatched (non-comment) frame with data. */
  onFrame: (frame: SseFrame) => void;
  /** A `:ping` keep-alive or any comment. */
  onComment?: () => void;
  /** Connection established (response headers received). */
  onOpen?: () => void;
  /** Stream dropped or failed; `err` may be an {@link SseHttpError}. */
  onDisconnect?: (err: unknown) => void;
  /** About to sleep before the next reconnect attempt (`attempt` is 1-based). */
  onReconnecting?: (attempt: number, delayMs: number) => void;
  /** Resume point for reconnects. */
  getLastEventId?: () => string | null;
  baseBackoffMs?: number;
  maxBackoffMs?: number;
  /** If no frame/comment arrives within this window, force a reconnect. */
  watchdogMs?: number;
}

function abortableSleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve();
    const t = setTimeout(done, ms);
    function done() {
      clearTimeout(t);
      signal.removeEventListener('abort', done);
      resolve();
    }
    signal.addEventListener('abort', done, { once: true });
  });
}

export class SseClient {
  private readonly opts: Required<
    Pick<SseClientOptions, 'baseBackoffMs' | 'maxBackoffMs' | 'watchdogMs'>
  > &
    SseClientOptions;
  private controller: AbortController | null = null;
  private lifecycle: AbortController | null = null;
  private stopped = true;
  private attempt = 0;
  private lastActivity = 0;
  private watchdog: ReturnType<typeof setInterval> | null = null;

  constructor(opts: SseClientOptions) {
    this.opts = {
      baseBackoffMs: 1000,
      maxBackoffMs: 30000,
      watchdogMs: 35000,
      ...opts,
    };
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.attempt = 0;
    this.lifecycle = new AbortController();
    this.startWatchdog();
    void this.loop();
  }

  stop(): void {
    this.stopped = true;
    if (this.watchdog) {
      clearInterval(this.watchdog);
      this.watchdog = null;
    }
    this.lifecycle?.abort();
    this.controller?.abort();
    this.controller = null;
  }

  private startWatchdog(): void {
    this.watchdog = setInterval(() => {
      if (this.stopped) return;
      if (this.lastActivity && Date.now() - this.lastActivity > this.opts.watchdogMs) {
        // Silently-dead stream: abort the in-flight fetch so the loop reconnects.
        this.controller?.abort();
      }
    }, 5000);
  }

  private async loop(): Promise<void> {
    const life = this.lifecycle;
    while (!this.stopped && life && !life.signal.aborted) {
      const ctrl = new AbortController();
      this.controller = ctrl;
      try {
        const headers: Record<string, string> = { Accept: 'text/event-stream' };
        const lastId = this.opts.getLastEventId?.();
        if (lastId) headers['Last-Event-ID'] = lastId;

        const res = await fetch(this.opts.url, {
          method: 'GET',
          headers,
          credentials: 'include',
          cache: 'no-store',
          signal: ctrl.signal,
        });

        if (!res.ok || !res.body) {
          throw new SseHttpError(res.status);
        }

        this.attempt = 0;
        this.lastActivity = Date.now();
        this.opts.onOpen?.();

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        const parser = new SSEParser();

        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          this.lastActivity = Date.now();
          const frames = parser.push(decoder.decode(value, { stream: true }));
          for (const f of frames) {
            if (f.comment) this.opts.onComment?.();
            else this.opts.onFrame(f);
          }
        }
        // Server closed the stream cleanly → reconnect.
      } catch (err) {
        if (this.stopped) break;
        // An abort we triggered (watchdog/stop) is not a real error to report.
        const aborted = ctrl.signal.aborted && !(err instanceof SseHttpError);
        if (!aborted) this.opts.onDisconnect?.(err);
      }

      if (this.stopped || (life && life.signal.aborted)) break;

      this.attempt += 1;
      const delay = this.backoffDelay(this.attempt);
      this.opts.onReconnecting?.(this.attempt, delay);
      await abortableSleep(delay, life.signal);
    }
  }

  private backoffDelay(attempt: number): number {
    const exp = Math.min(this.opts.maxBackoffMs, this.opts.baseBackoffMs * 2 ** (attempt - 1));
    // Full jitter to avoid a thundering herd of reconnecting dashboards.
    return Math.round(exp / 2 + Math.random() * (exp / 2));
  }
}
